import React, { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../components/common/Toast';
import { Modal } from '../../components/common/Modal';
import { PageHeader } from '../../components/common/PageHeader';
import { FinancialTransaction, TransactionType, GelanggangDetail, PersatuanPaymentSettings, Gelanggang } from '../../types';
import { verifyToyyibPayBillStatus } from '../../lib/toyyibpay';
import {
  getTransactionsDB,
  saveTransactionDB,
  deleteTransactionDB,
  deleteTransactionsBulkDB,
  autoSyncPaidInvoicesToTransactions,
  getInvoicesDB,
  deduplicateTransactions,
  getGelanggangDB,
  getPersatuanPaymentSettingsDB,
  DEFAULT_PERSATUAN_PAYMENT_SETTINGS
} from '../../services/db';
import { isSuperAdmin, hasRolePermission } from '../../utils/formatters';
import { resolveCloudinaryUrl } from '../../lib/cloudinary';
import {
  FileSpreadsheet,
  Plus,
  TrendingUp,
  TrendingDown,
  Printer,
  FileCheck2,
  FolderGit2,
  Trash2,
  RefreshCw,
  CheckCircle2,
  ShieldAlert,
  Download,
  Landmark,
  Building2,
  Wallet,
  CreditCard
} from 'lucide-react';

export type BankAccountKey = 'all' | 'persatuan' | 'pusaka_warisan' | 'wadi_annour';

export function getTransactionBankAccountKey(tx: FinancialTransaction): 'persatuan' | 'pusaka_warisan' | 'wadi_annour' {
  if ((tx as any).bankAccount) {
    const raw = String((tx as any).bankAccount).toLowerCase();
    if (raw.includes('pusaka')) return 'pusaka_warisan';
    if (raw.includes('wadi')) return 'wadi_annour';
    if (raw.includes('persatuan') || raw.includes('asmm') || raw.includes('induk')) return 'persatuan';
  }

  // Mandatory Persatuan fees (ROS legal mandate)
  if (
    tx.category === 'Yuran Pendaftaran' ||
    tx.category === 'Yuran Bengkung' ||
    tx.category === 'Acara & Sijil' ||
    tx.category === 'Sumbangan Syarikat' ||
    tx.category === 'Sewa Tapak / Vendor' ||
    tx.category === 'Pihak Ketiga / Vendor'
  ) {
    return 'persatuan';
  }

  const g = (tx.gelanggang || '').trim().toLowerCase();
  const desc = (tx.description || '').toLowerCase();
  const payer = (tx.payerOrPayee || '').toLowerCase();

  if (g.includes('wadi') || desc.includes('wadi annour') || desc.includes('wadi an-nour') || payer.includes('wadi annour')) {
    return 'wadi_annour';
  }

  if (g.includes('pusaka') || desc.includes('pusaka warisan') || payer.includes('pusaka warisan')) {
    return 'pusaka_warisan';
  }

  return 'persatuan';
}

export const AuditReportPage: React.FC = () => {
  const { currentUser } = useAuth();
  const { showToast } = useToast();

  const isExecutive = currentUser && (isSuperAdmin(currentUser) || hasRolePermission(currentUser, ['Pengerusi', 'Timbalan Pengerusi', 'Setiausaha', 'Pen. Setiausaha', 'Bendahari', 'Pemeriksa Kira-Kira']));

  // Helper to read initial account and filter type from URL query
  const getInitialFilterType = (): string => {
    if (typeof window === 'undefined') return 'all';
    const params = new URLSearchParams(window.location.search);
    const t = (params.get('type') || params.get('tab') || '').toLowerCase().trim();
    if (t === 'income' || t === 'masuk') return 'Income';
    if (t === 'expense' || t === 'keluar') return 'Expense';
    return 'all';
  };

  const getInitialAccountKey = (): BankAccountKey => {
    if (typeof window === 'undefined') return 'all';
    const params = new URLSearchParams(window.location.search);
    const a = (params.get('account') || params.get('bank') || '').toLowerCase().trim();
    if (a === 'persatuan') return 'persatuan';
    if (a === 'pusaka_warisan' || a === 'pusaka-warisan' || a === 'pusaka') return 'pusaka_warisan';
    if (a === 'wadi_annour' || a === 'wadi-annour' || a === 'wadi') return 'wadi_annour';
    return 'all';
  };

  const [transactions, setTransactions] = useState<FinancialTransaction[]>([]);
  const [gelanggangList, setGelanggangList] = useState<GelanggangDetail[]>([]);
  const [persatuanSettings, setPersatuanSettings] = useState<PersatuanPaymentSettings>(DEFAULT_PERSATUAN_PAYMENT_SETTINGS);
  const [selectedAccountKey, setSelectedAccountKey] = useState<BankAccountKey>(getInitialAccountKey);
  const [newTxAccount, setNewTxAccount] = useState<'persatuan' | 'pusaka_warisan' | 'wadi_annour' | 'paya_besar'>('persatuan');

  const [filterType, setFilterType] = useState<string>(getInitialFilterType);
  const [selectedTxIds, setSelectedTxIds] = useState<Set<string>>(new Set());
  const [isBulkDeleteModalOpen, setIsBulkDeleteModalOpen] = useState<boolean>(false);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);
  const [isSyncingAudit, setIsSyncingAudit] = useState<boolean>(false);

  // Handlers to update URL when facets change
  const handleSelectAccountKey = (key: BankAccountKey) => {
    setSelectedAccountKey(key);
    setSelectedTxIds(new Set());
    if (typeof window !== 'undefined') {
      const url = new URL(window.location.href);
      if (key === 'all') {
        url.searchParams.delete('account');
        url.searchParams.delete('bank');
      } else {
        url.searchParams.set('account', key);
      }
      window.history.pushState({ account: key, type: filterType }, '', url.toString());
    }
  };

  const handleSelectFilterType = (newType: string) => {
    setFilterType(newType);
    setSelectedTxIds(new Set());
    if (typeof window !== 'undefined') {
      const url = new URL(window.location.href);
      if (newType === 'all') {
        url.searchParams.delete('type');
        url.searchParams.delete('tab');
      } else {
        url.searchParams.set('type', newType.toLowerCase());
      }
      window.history.pushState({ account: selectedAccountKey, type: newType }, '', url.toString());
    }
  };

  useEffect(() => {
    getGelanggangDB().then(setGelanggangList).catch(() => {});
    getPersatuanPaymentSettingsDB().then(setPersatuanSettings).catch(() => {});
  }, []);

  useEffect(() => {
    if (isExecutive) {
      // Auto sync paid invoices to audit ledger on mount
      autoSyncPaidInvoicesToTransactions().then((data) => {
        setTransactions(data);

        // Auto verify pending ToyyibPay transactions in background
        const pending = data.filter(t => t.toyibBillCode && t.status === 'Menunggu Pengesahan');
        if (pending.length > 0) {
          Promise.all(
            pending.map(async (tx) => {
              try {
                const res = await verifyToyyibPayBillStatus(tx.toyibBillCode!);
                if (res.status !== tx.status) {
                  const updatedTx: FinancialTransaction = { ...tx, status: res.status };
                  await saveTransactionDB(updatedTx);
                  return updatedTx;
                }
              } catch (e) {
                console.error("Auto verify transaction failed:", e);
              }
              return null;
            })
          ).then((results) => {
            const hasUpdates = results.some(r => r !== null);
            if (hasUpdates) {
              getTransactionsDB().then(setTransactions);
            }
          });
        }
      }).catch(() => {
        getTransactionsDB().then(setTransactions).catch(() => { });
      });
    }

    const handleDbUpdate = () => {
      getTransactionsDB().then(setTransactions).catch(() => { });
    };
    window.addEventListener('asmm_db_updated', handleDbUpdate);
    return () => window.removeEventListener('asmm_db_updated', handleDbUpdate);
  }, [isExecutive]);

  // Synchronize browser URL Back/Forward navigation with active facets
  useEffect(() => {
    const handlePopState = () => {
      const search = new URLSearchParams(window.location.search);

      // Sync filter type
      const t = (search.get('type') || search.get('tab') || '').toLowerCase().trim();
      if (t === 'income' || t === 'masuk') {
        setFilterType('Income');
      } else if (t === 'expense' || t === 'keluar') {
        setFilterType('Expense');
      } else {
        setFilterType('all');
      }

      // Sync account
      const a = (search.get('account') || search.get('bank') || '').toLowerCase().trim();
      if (a === 'persatuan') {
        setSelectedAccountKey('persatuan');
      } else if (a === 'pusaka_warisan' || a === 'pusaka-warisan' || a === 'pusaka') {
        setSelectedAccountKey('pusaka_warisan');
      } else if (a === 'wadi_annour' || a === 'wadi-annour' || a === 'wadi') {
        setSelectedAccountKey('wadi_annour');
      } else {
        setSelectedAccountKey('all');
      }

      setSelectedTxIds(new Set());
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  const handleManualSync = async () => {
    setIsSyncingAudit(true);
    try {
      const fresh = await autoSyncPaidInvoicesToTransactions();
      setTransactions(fresh);
      showToast('success', 'Segerak Selesai', 'Buku tunai dan rekod audit telah disegerakkan dengan semua invois bayaran terkini.');
    } catch (err) {
      console.error('Audit sync error:', err);
      showToast('error', 'Ralat Segerak', 'Gagal menyegerakkan rekod bayaran audit.');
    } finally {
      setIsSyncingAudit(false);
    }
  };

  if (!isExecutive) {
    return (
      <div className="bg-white rounded-3xl p-12 text-center border border-slate-200 shadow-sm max-w-lg mx-auto mt-10">
        <ShieldAlert className="w-16 h-16 text-rose-500 mx-auto mb-4" />
        <h3 className="text-lg font-bold text-slate-800">Akses Terhad</h3>
        <p className="text-xs text-slate-500 mt-2">
          Penyata kewangan beraudit dan rekod transaksi rasmi hanya boleh diakses oleh Ahli Jawatankuasa Eksekutif ASMM.
        </p>
      </div>
    );
  }

  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [selectedReceiptTx, setSelectedReceiptTx] = useState<FinancialTransaction | null>(null);
  const [activeReceiptUrl, setActiveReceiptUrl] = useState<string>('');
  const [category, setCategory] = useState<any>('Yuran Bulanan');
  const [description, setDescription] = useState('');
  const [amount, setAmount] = useState<number>(50);
  const [type, setType] = useState<TransactionType>('Income');
  const [payerOrPayee, setPayerOrPayee] = useState('');
  const [receiptUrl, setReceiptUrl] = useState('');

  const handleViewReceipt = async (tx: FinancialTransaction) => {
    setSelectedReceiptTx(tx);
    let resolvedUrl = tx.receiptUrl || '';

    // If not a data URL or external link, look up corresponding invoice
    if (!resolvedUrl.startsWith('data:') && !resolvedUrl.startsWith('http')) {
      try {
        const invList = await getInvoicesDB();
        const matchedInv = invList.find(i =>
          (tx.toyibBillCode && (i.toyibBillCode === tx.toyibBillCode || i.invoiceNo === tx.toyibBillCode)) ||
          (tx.description && i.invoiceNo && tx.description.includes(i.invoiceNo))
        );
        if (matchedInv?.receiptUrl) {
          resolvedUrl = matchedInv.receiptUrl;
        }
      } catch (e) {
        console.warn('Failed resolving invoice receipt:', e);
      }
    }

    resolvedUrl = resolveCloudinaryUrl(resolvedUrl);
    setActiveReceiptUrl(resolvedUrl);
  };

  const canEdit = currentUser && (isSuperAdmin(currentUser) || hasRolePermission(currentUser, ['Pengerusi', 'Timbalan Pengerusi', 'Bendahari']));

  const pusakaDetail = gelanggangList.find(g => g.name.toLowerCase().includes('pusaka'));
  const wadiDetail = gelanggangList.find(g => g.name.toLowerCase().includes('wadi'));
  const payaDetail = gelanggangList.find(g => g.name.toLowerCase().includes('paya'));

  const bankAccounts = useMemo(() => [
    {
      key: 'all' as BankAccountKey,
      label: 'Semua Akaun Bank (Konsolidasi)',
      shortLabel: 'Semua Akaun',
      bankName: 'Semua Bank (Konsolidasi)',
      accountNo: 'Penyata Gabungan',
      accountHolder: 'Persatuan Seni Silat Awang Sulung Merah Muda Kedah',
      badge: 'Konsolidasi',
      icon: Wallet
    },
    {
      key: 'persatuan' as BankAccountKey,
      label: 'Akaun Induk Persatuan (Bank Islam)',
      shortLabel: 'Persatuan',
      bankName: persatuanSettings.bankName || 'Bank Islam Malaysia Berhad',
      accountNo: persatuanSettings.accountNo || '02011010087654',
      accountHolder: persatuanSettings.accountHolder || 'PERSATUAN SENI SILAT AWANG SULUNG MERAH MUDA NEGERI KEDAH',
      badge: 'Akaun Utama ROS',
      icon: Landmark
    },
    {
      key: 'pusaka_warisan' as BankAccountKey,
      label: 'Gelanggang Pusaka Warisan (Maybank)',
      shortLabel: 'Pusaka Warisan',
      bankName: pusakaDetail?.qrBankName || 'Maybank',
      accountNo: pusakaDetail?.qrAccountNo || '152068654321',
      accountHolder: pusakaDetail?.qrAccountName || pusakaDetail?.leaderName || 'Pusaka Warisan',
      badge: 'Akaun Gelanggang',
      icon: Building2
    },
    {
      key: 'wadi_annour' as BankAccountKey,
      label: 'Gelanggang Wadi AnNour (Bank Islam)',
      shortLabel: 'Wadi AnNour',
      bankName: wadiDetail?.qrBankName || 'Bank Islam',
      accountNo: wadiDetail?.qrAccountNo || '02093010041234',
      accountHolder: wadiDetail?.qrAccountName || wadiDetail?.leaderName || 'Wadi AnNour',
      badge: 'Akaun Gelanggang',
      icon: Building2
    }
  ], [persatuanSettings, pusakaDetail, wadiDetail]);

  const selectedAccount = useMemo(() => {
    return bankAccounts.find(a => a.key === selectedAccountKey) || bankAccounts[0];
  }, [bankAccounts, selectedAccountKey]);

  // Clean transactions and resolve bank account key for each
  const cleanTransactions = useMemo(() => {
    const rawClean = deduplicateTransactions(transactions).clean;
    return rawClean.map(tx => {
      let desc = tx.description || '';
      if (desc.includes('Disahkan Pentadbiran') || desc.includes('Disahkan Superadmin')) {
        const confirmer = (tx.recordedBy && !tx.recordedBy.toLowerCase().includes('auto-sync') && !tx.recordedBy.toLowerCase().includes('sistem'))
          ? tx.recordedBy
          : (currentUser?.name || 'Mohammad Hafiz Bin Halim');
        desc = desc.replace(/\(Disahkan\s+Pentadbiran\s*\/\s*Superadmin\)/gi, `(Disahkan oleh ${confirmer})`)
                   .replace(/\(Disahkan\s+Pentadbiran\)/gi, `(Disahkan oleh ${confirmer})`)
                   .replace(/\(Disahkan\s+Superadmin\)/gi, `(Disahkan oleh ${confirmer})`);
      }
      const bankKey = getTransactionBankAccountKey(tx);
      return { ...tx, description: desc, resolvedBankKey: bankKey };
    });
  }, [transactions, currentUser]);

  // Filter transactions by selected bank account
  const accountTransactions = useMemo(() => {
    if (selectedAccountKey === 'all') return cleanTransactions;
    return cleanTransactions.filter(t => t.resolvedBankKey === selectedAccountKey);
  }, [cleanTransactions, selectedAccountKey]);

  // Per-account stats map for summary cards and selectors
  const accountStatsMap = useMemo(() => {
    const map: Record<BankAccountKey, { count: number; income: number; expense: number; net: number }> = {
      all: { count: cleanTransactions.length, income: 0, expense: 0, net: 0 },
      persatuan: { count: 0, income: 0, expense: 0, net: 0 },
      pusaka_warisan: { count: 0, income: 0, expense: 0, net: 0 },
      wadi_annour: { count: 0, income: 0, expense: 0, net: 0 },
    };

    cleanTransactions.forEach(tx => {
      const k = tx.resolvedBankKey as BankAccountKey;
      if (map[k]) {
        map[k].count++;
        if (tx.type === 'Income') map[k].income += tx.amount;
        else if (tx.type === 'Expense') map[k].expense += tx.amount;
        map[k].net = map[k].income - map[k].expense;
      }
      if (tx.type === 'Income') map.all.income += tx.amount;
      else if (tx.type === 'Expense') map.all.expense += tx.amount;
    });
    map.all.net = map.all.income - map.all.expense;

    return map;
  }, [cleanTransactions]);

  const getBankBadge = (bankKey?: BankAccountKey) => {
    switch (bankKey) {
      case 'persatuan':
        return {
          label: 'Persatuan',
          sub: 'Bank Islam',
          bg: 'bg-red-50 text-red-700 border-red-200/80',
          dot: 'bg-red-600',
        };
      case 'pusaka_warisan':
        return {
          label: 'Pusaka Warisan',
          sub: 'Maybank',
          bg: 'bg-amber-50 text-amber-800 border-amber-200/80',
          dot: 'bg-amber-500',
        };
      case 'wadi_annour':
        return {
          label: 'Wadi AnNour',
          sub: 'Bank Islam',
          bg: 'bg-blue-50 text-blue-800 border-blue-200/80',
          dot: 'bg-blue-600',
        };
      default:
        return {
          label: 'Persatuan',
          sub: 'Induk',
          bg: 'bg-slate-100 text-slate-700 border-slate-200',
          dot: 'bg-slate-500',
        };
    }
  };

  // Metrics for active account view
  const totalIncome = accountTransactions.filter(t => t.type === 'Income').reduce((s, t) => s + t.amount, 0);
  const totalExpense = accountTransactions.filter(t => t.type === 'Expense').reduce((s, t) => s + t.amount, 0);
  const netBalance = totalIncome - totalExpense;

  const incomeCount = accountTransactions.filter(t => t.type === 'Income').length;
  const expenseCount = accountTransactions.filter(t => t.type === 'Expense').length;

  // Filter by transaction type ('all' | 'Income' | 'Expense')
  const filteredTransactions = useMemo(() => {
    if (filterType === 'all') return accountTransactions;
    return accountTransactions.filter(t => t.type === filterType);
  }, [accountTransactions, filterType]);

  const selectedTransactions = filteredTransactions.filter(t => selectedTxIds.has(t.id));
  const selectedAmountTotal = selectedTransactions.reduce((sum, t) => sum + t.amount, 0);

  const printTransactions = selectedTxIds.size > 0
    ? filteredTransactions.filter(t => selectedTxIds.has(t.id))
    : filteredTransactions;

  const printIncomeTotal = printTransactions
    .filter(t => t.type === 'Income')
    .reduce((sum, t) => sum + t.amount, 0);

  const printExpenseTotal = printTransactions
    .filter(t => t.type === 'Expense')
    .reduce((sum, t) => sum + t.amount, 0);

  const handleToggleSelectAll = () => {
    if (selectedTxIds.size === filteredTransactions.length && filteredTransactions.length > 0) {
      setSelectedTxIds(new Set());
    } else {
      setSelectedTxIds(new Set(filteredTransactions.map(t => t.id)));
    }
  };

  const handleToggleSelectOne = (id: string, e: React.MouseEvent | React.ChangeEvent) => {
    e.stopPropagation();
    setSelectedTxIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handleSaveTransaction = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!description.trim() || amount <= 0) {
      showToast('error', 'Sila isi penerangan transaksi dan jumlah wang');
      return;
    }

    const currentScrollY = window.scrollY;
    const currentScrollX = window.scrollX;

    let targetGelanggang: Gelanggang = 'ASMM';
    if (newTxAccount === 'pusaka_warisan') targetGelanggang = 'Pusaka Warisan';
    else if (newTxAccount === 'wadi_annour') targetGelanggang = 'Wadi AnNour';
    else targetGelanggang = 'ASMM';

    const newTx: FinancialTransaction = {
      id: `ASMM-TX-${Date.now()}`,
      date: new Date().toISOString().split('T')[0],
      category,
      description,
      amount,
      type,
      payerOrPayee: payerOrPayee || 'Ahli Persatuan',
      gelanggang: targetGelanggang,
      recordedBy: currentUser?.name || 'Bendahari',
      status: 'Disahkan',
      receiptUrl: receiptUrl || 'gdrive-management-file'
    };
    (newTx as any).bankAccount = newTxAccount;

    const updated = await saveTransactionDB(newTx);
    setTransactions(updated);
    showToast('success', 'Transaksi Kewangan Disimpan!', `Buku Tunai dikemaskini: RM ${amount}`);
    setIsModalOpen(false);
    requestAnimationFrame(() => {
      window.scrollTo({ top: currentScrollY, left: currentScrollX, behavior: 'instant' });
    });
  };

  const handlePrintAuditReport = () => {
    window.print();
  };

  const [confirmDeleteTxId, setConfirmDeleteTxId] = useState<{ id: string; desc: string } | null>(null);

  const handleDeleteTx = async (id: string, desc: string) => {
    setConfirmDeleteTxId({ id, desc });
  };

  const confirmDeleteTx = async () => {
    if (!confirmDeleteTxId) return;
    const id = confirmDeleteTxId.id;
    const desc = confirmDeleteTxId.desc;
    const currentScrollY = window.scrollY;
    const currentScrollX = window.scrollX;
    setIsDeleting(true);

    try {
      setTransactions(prev => prev.filter(t => t.id !== id));
      setSelectedTxIds(prev => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });

      await deleteTransactionDB(id);
      showToast('info', 'Rekod Kewangan Dipadam', desc);
      setConfirmDeleteTxId(null);
    } catch (err) {
      console.error("Delete failed:", err);
      showToast('error', 'Ralat', 'Gagal memadam rekod transaksi.');
    } finally {
      setIsDeleting(false);
      requestAnimationFrame(() => {
        window.scrollTo({ top: currentScrollY, left: currentScrollX, behavior: 'instant' });
      });
    }
  };

  const confirmBulkDelete = async () => {
    if (selectedTxIds.size === 0) return;
    const idsToDelete = Array.from(selectedTxIds);
    const count = idsToDelete.length;
    const currentScrollY = window.scrollY;
    const currentScrollX = window.scrollX;
    setIsDeleting(true);

    try {
      // 1. Optimistic state update via AJAX without reload
      setTransactions(prev => prev.filter(t => !selectedTxIds.has(t.id)));
      setSelectedTxIds(new Set());

      // 2. Persistent bulk deletion in aaPanel DB & localStorage
      await deleteTransactionsBulkDB(idsToDelete);

      showToast('success', 'Transaksi Dipadam Pukal', `${count} rekod transaksi kewangan telah berjaya dipadam.`);
      setIsBulkDeleteModalOpen(false);
    } catch (err) {
      console.error("Bulk delete failed:", err);
      showToast('error', 'Ralat', 'Gagal memadam rekod transaksi terpilih.');
    } finally {
      setIsDeleting(false);
      requestAnimationFrame(() => {
        window.scrollTo({ top: currentScrollY, left: currentScrollX, behavior: 'instant' });
      });
    }
  };

  return (
    <div className="w-full space-y-4 sm:space-y-6 min-h-[75vh]">
      {/* Fixed Header Banner */}
      <PageHeader
        badgeText="Audit"
        badgeIcon={FileCheck2}
        title="Penyata & Buku Tunai"
      >
        <div className="flex items-center gap-2 w-full sm:w-auto flex-wrap sm:flex-nowrap">
          <button
            type="button"
            onClick={handleManualSync}
            disabled={isSyncingAudit}
            className="flex-1 sm:flex-initial px-3.5 sm:px-4 py-2 sm:py-2.5 bg-blue-50 hover:bg-blue-100 text-blue-800 font-bold text-xs rounded-xl border border-blue-200 transition-all flex items-center justify-center gap-1.5 cursor-pointer shadow-2xs disabled:opacity-50"
            title="Segerak semua rekod bayaran invois dan buku tunai audit"
          >
            <RefreshCw className={`w-3.5 h-3.5 text-blue-600 ${isSyncingAudit ? 'animate-spin' : ''}`} />
            <span>{isSyncingAudit ? 'Menyegerak...' : 'Segerak Bayaran & Audit'}</span>
          </button>

          <button
            onClick={handlePrintAuditReport}
            className="flex-1 sm:flex-initial px-3.5 sm:px-4 py-2 sm:py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-800 font-bold text-xs rounded-xl border border-slate-200 transition-all flex items-center justify-center gap-2 cursor-pointer shadow-2xs"
            title="Cetak Penyata & Buku Tunai Rasmi (Format ROS)"
          >
            <Printer className="w-4 h-4 text-asmm-red" />
            <span>{selectedTxIds.size > 0 ? `Cetak Laporan (${selectedTxIds.size})` : 'Cetak Laporan'}</span>
          </button>

          {canEdit && (
            <button
              onClick={() => {
                if (selectedAccountKey !== 'all') {
                  setNewTxAccount(selectedAccountKey as any);
                }
                setIsModalOpen(true);
              }}
              className="flex-1 sm:flex-initial px-3.5 sm:px-4 py-2 sm:py-2.5 bg-asmm-red hover:bg-asmm-crimson text-white font-bold text-xs rounded-xl transition-all shadow-xs flex items-center justify-center gap-1.5 cursor-pointer"
            >
              <Plus className="w-4 h-4" />
              <span>Tambah Rekod</span>
            </button>
          )}
        </div>
      </PageHeader>

      {/* Bank Account Selector Tabs / Cards */}
      <div className="space-y-2.5 no-print">
        <div className="flex items-center justify-between">
          <div className="text-xs font-black uppercase tracking-wider text-slate-800 flex items-center gap-1.5">
            <Landmark className="w-3.5 h-3.5 text-asmm-red" />
            <span>Pilih Akaun Bank / Entiti Silat</span>
          </div>
          <span className="text-[11px] text-slate-500 font-medium">
            {selectedAccountKey === 'all' ? 'Paparan Konsolidasi (Semua Akaun)' : `Akaun: ${selectedAccount.shortLabel}`}
          </span>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-2.5 sm:gap-3">
          {bankAccounts.map((acc) => {
            const isSelected = selectedAccountKey === acc.key;
            const stats = accountStatsMap[acc.key] || { count: 0, net: 0, income: 0, expense: 0 };
            const IconComp = acc.icon;

            return (
              <button
                key={acc.key}
                type="button"
                onClick={() => {
                  handleSelectAccountKey(acc.key);
                }}
                className={`p-3 sm:p-3.5 rounded-2xl border text-left transition-all cursor-pointer relative flex flex-col justify-between ${
                  isSelected
                    ? 'bg-slate-900 text-white border-slate-900 shadow-md ring-2 ring-slate-900/10'
                    : 'bg-white hover:bg-slate-50 text-slate-800 border-slate-200 shadow-2xs'
                }`}
              >
                <div>
                  <div className="flex items-center justify-between gap-1.5 mb-1.5">
                    <div className={`p-1.5 rounded-xl ${isSelected ? 'bg-white/10 text-white' : 'bg-slate-100 text-slate-700'}`}>
                      <IconComp className="w-4 h-4" />
                    </div>
                    <span
                      className={`text-[10px] font-mono font-bold px-2 py-0.5 rounded-full ${
                        isSelected ? 'bg-white/20 text-white' : 'bg-slate-100 text-slate-600'
                      }`}
                    >
                      {stats.count} rekod
                    </span>
                  </div>
                  <div className={`text-xs font-black truncate leading-tight ${isSelected ? 'text-white' : 'text-slate-900'}`}>
                    {acc.shortLabel}
                  </div>
                  <div className={`text-[10px] font-medium truncate mt-0.5 ${isSelected ? 'text-slate-300' : 'text-slate-500'}`}>
                    {acc.bankName.replace(' Malaysia Berhad', '')}
                  </div>
                </div>

                <div className={`mt-2.5 pt-2 border-t flex items-center justify-between ${isSelected ? 'border-white/15' : 'border-slate-100'}`}>
                  <span className={`text-[10px] font-bold ${isSelected ? 'text-slate-300' : 'text-slate-400'}`}>
                    Baki
                  </span>
                  <span
                    className={`text-xs font-black font-mono ${
                      isSelected
                        ? 'text-emerald-300'
                        : stats.net >= 0
                        ? 'text-emerald-700'
                        : 'text-rose-600'
                    }`}
                  >
                    RM {stats.net.toLocaleString('ms-MY')}
                  </span>
                </div>
              </button>
            );
          })}
        </div>

        {/* Active Bank Account Specific Info Banner */}
        {selectedAccountKey !== 'all' && (
          <div className="p-3.5 sm:p-4 bg-slate-50 rounded-2xl border border-slate-200 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 animate-fade-in">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-2xl bg-white border border-slate-200 shadow-2xs flex items-center justify-center shrink-0">
                <Landmark className="w-5 h-5 text-asmm-red" />
              </div>
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h4 className="text-xs sm:text-sm font-black text-slate-900 truncate">{selectedAccount.label}</h4>
                  <span className="text-[10px] font-bold bg-white text-slate-700 px-2 py-0.5 rounded-full border border-slate-200 shrink-0">
                    {selectedAccount.badge}
                  </span>
                </div>
                <div className="text-[11px] font-mono text-slate-600 mt-0.5 truncate">
                  <span>{selectedAccount.bankName}</span> • No. Akaun: <strong className="text-slate-900">{selectedAccount.accountNo}</strong> ({selectedAccount.accountHolder})
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button
                type="button"
                onClick={() => setSelectedAccountKey('all')}
                className="px-3 py-1.5 text-[11px] font-bold bg-white hover:bg-slate-100 text-slate-700 rounded-xl border border-slate-200 transition-all cursor-pointer shadow-3xs"
              >
                Tunjuk Semua Akaun
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Filter Chips Bar with Count Badges */}
      <div className="flex items-center gap-2 overflow-x-auto no-scrollbar flex-nowrap pb-1 no-print">
        {[
          { id: 'all', label: 'Semua', count: accountTransactions.length },
          { id: 'Income', label: 'Masuk', count: incomeCount },
          { id: 'Expense', label: 'Keluar', count: expenseCount },
        ].map((item) => (
          <button
            key={item.id}
            onClick={() => handleSelectFilterType(item.id)}
            className={`px-3.5 sm:px-4 py-1.5 sm:py-2 rounded-xl text-xs font-bold whitespace-nowrap shrink-0 transition-all flex items-center gap-1.5 cursor-pointer ${filterType === item.id
              ? 'bg-slate-900 text-white shadow-xs font-extrabold'
              : 'bg-white text-slate-600 hover:bg-slate-100 border border-slate-200'
              }`}
          >
            <span>{item.label}</span>
            <span
              className={`text-[10px] px-1.5 py-0.5 rounded-full font-mono font-bold ${filterType === item.id ? 'bg-white/20 text-white' : 'bg-slate-100 text-slate-600'
                }`}
            >
              {item.count}
            </span>
          </button>
        ))}
      </div>

      {/* Summary Cards - Responsive: 3 columns on mobile with compact metrics, full cards on desktop */}
      <div className="grid grid-cols-3 gap-2 sm:gap-4">
        {/* Income Card */}
        <div className="bg-white p-3 sm:p-5 rounded-2xl sm:rounded-3xl border border-slate-200 shadow-xs flex flex-col justify-between">
          <div className="flex items-center justify-between gap-1 mb-1 sm:mb-2">
            <span className="text-[10px] sm:text-xs font-extrabold text-slate-500 uppercase tracking-wider truncate">
              Pendapatan
            </span>
            <div className="p-1 sm:p-2 bg-emerald-50 rounded-lg sm:rounded-xl text-emerald-600 border border-emerald-100 shrink-0">
              <TrendingUp className="w-3 h-3 sm:w-4 sm:h-4" />
            </div>
          </div>
          <div className="text-xs sm:text-2xl font-black text-emerald-700 font-mono truncate">
            RM {totalIncome.toLocaleString('ms-MY')}
          </div>
          <p className="hidden sm:block text-[11px] text-slate-500 mt-1 truncate">Yuran Bulanan & Sumbangan</p>
        </div>

        {/* Expense Card */}
        <div className="bg-white p-3 sm:p-5 rounded-2xl sm:rounded-3xl border border-slate-200 shadow-xs flex flex-col justify-between">
          <div className="flex items-center justify-between gap-1 mb-1 sm:mb-2">
            <span className="text-[10px] sm:text-xs font-extrabold text-slate-500 uppercase tracking-wider truncate">
              Perbelanjaan
            </span>
            <div className="p-1 sm:p-2 bg-rose-50 rounded-lg sm:rounded-xl text-rose-600 border border-rose-100 shrink-0">
              <TrendingDown className="w-3 h-3 sm:w-4 sm:h-4" />
            </div>
          </div>
          <div className="text-xs sm:text-2xl font-black text-rose-700 font-mono truncate">
            RM {totalExpense.toLocaleString('ms-MY')}
          </div>
          <p className="hidden sm:block text-[11px] text-slate-500 mt-1 truncate">Sewa Gelanggang & Alatan</p>
        </div>

        {/* Net Balance Card */}
        <div className="bg-white p-3 sm:p-5 rounded-2xl sm:rounded-3xl border border-slate-200 shadow-xs flex flex-col justify-between">
          <div className="flex items-center justify-between gap-1 mb-1 sm:mb-2">
            <span className="text-[10px] sm:text-xs font-extrabold text-slate-500 uppercase tracking-wider truncate">
              Baki Bersih
            </span>
            <div className="p-1 sm:p-2 bg-slate-100 rounded-lg sm:rounded-xl text-slate-800 border border-slate-200 shrink-0">
              <FileSpreadsheet className="w-3 h-3 sm:w-4 sm:h-4" />
            </div>
          </div>
          <div className="text-xs sm:text-2xl font-black text-slate-900 font-mono truncate">
            RM {netBalance.toLocaleString('ms-MY')}
          </div>
          <p className="hidden sm:block text-[11px] text-slate-500 mt-1 truncate">Disahkan Oleh Auditor</p>
        </div>
      </div>

      {/* Bulk Action Alert Bar - Responsive Sticky */}
      {canEdit && selectedTxIds.size > 0 && (
        <div className="bg-white/95 border border-rose-200 rounded-2xl p-3 sm:px-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 animate-fade-in no-print shadow-lg sticky top-14 sm:top-20 z-30 backdrop-blur-md">
          <div className="flex items-center gap-3">
            <span className="w-8 h-8 rounded-xl bg-rose-100 text-rose-700 font-black text-xs flex items-center justify-center border border-rose-200 shadow-2xs shrink-0">
              {selectedTxIds.size}
            </span>
            <div className="min-w-0">
              <div className="text-xs font-bold text-slate-900 truncate">
                {selectedTxIds.size} rekod transaksi dipilih
              </div>
              <div className="text-[11px] text-rose-700 font-medium truncate">
                Jumlah: <strong className="font-mono font-bold">RM {selectedAmountTotal.toLocaleString('ms-MY')}.00</strong>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 w-full sm:w-auto">
            <button
              type="button"
              onClick={handlePrintAuditReport}
              className="flex-1 sm:flex-initial px-3.5 py-2 bg-slate-100 hover:bg-slate-200 text-slate-800 text-xs font-bold rounded-xl border border-slate-200 transition-all cursor-pointer text-center shadow-2xs flex items-center justify-center gap-1.5"
            >
              <Printer className="w-3.5 h-3.5 text-asmm-red" />
              <span>Cetak ({selectedTxIds.size})</span>
            </button>
            <button
              type="button"
              onClick={() => setSelectedTxIds(new Set())}
              className="flex-1 sm:flex-initial px-3.5 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-bold rounded-xl border border-slate-200 transition-all cursor-pointer text-center shadow-2xs"
            >
              Batal Pilihan
            </button>
            <button
              type="button"
              onClick={() => setIsBulkDeleteModalOpen(true)}
              className="flex-1 sm:flex-initial px-4 py-2 bg-rose-600 hover:bg-rose-700 text-white text-xs font-extrabold rounded-xl transition-all shadow-xs flex items-center justify-center gap-1.5 cursor-pointer border-none"
            >
              <Trash2 className="w-3.5 h-3.5" />
              <span>Padam Pukal ({selectedTxIds.size})</span>
            </button>
          </div>
        </div>
      )}

      {/* Cashbook Ledger Container (Desktop Table & Native Mobile Cards) */}
      <div className="bg-white rounded-2xl sm:rounded-3xl border border-slate-200 shadow-xs overflow-hidden">
        {/* Header Bar */}
        <div className="p-4 sm:p-5 border-b border-slate-100 flex flex-wrap items-center justify-between gap-2">
          <div>
            <h3 className="text-sm sm:text-base font-bold text-slate-900 flex items-center gap-2">
              <span>Buku Tunai Transaksi Masuk / Keluar</span>
              <span className="text-[11px] font-bold text-slate-500 bg-slate-100 px-2.5 py-0.5 rounded-full border border-slate-200">
                {filteredTransactions.length} rekod
              </span>
            </h3>
            <p className="text-[11px] sm:text-xs text-slate-500 font-medium mt-0.5">
              Setiap rekod dilampirkan bukti resit rasmi pertubuhan
            </p>
          </div>
        </div>

        {/* 1. MOBILE NATIVE CARDS VIEW (< md) */}
        <div className="md:hidden">
          {canEdit && filteredTransactions.length > 0 && (
            <div className="p-3 bg-slate-50/80 border-b border-slate-100 flex items-center justify-between text-xs font-bold text-slate-600">
              <label className="flex items-center gap-2.5 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={filteredTransactions.length > 0 && selectedTxIds.size === filteredTransactions.length}
                  ref={(el) => {
                    if (el) {
                      el.indeterminate = selectedTxIds.size > 0 && selectedTxIds.size < filteredTransactions.length;
                    }
                  }}
                  onChange={handleToggleSelectAll}
                  className="w-4 h-4 rounded border-slate-300 text-asmm-red focus:ring-asmm-red cursor-pointer"
                />
                <span>Pilih Semua ({filteredTransactions.length})</span>
              </label>
              {selectedTxIds.size > 0 && (
                <span className="text-[11px] text-asmm-red font-extrabold">
                  {selectedTxIds.size} dipilih
                </span>
              )}
            </div>
          )}

          {filteredTransactions.length === 0 ? (
            <div className="p-8 text-center text-xs text-slate-500 font-medium">
              Tiada rekod transaksi dijumpai untuk kategori ini.
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {filteredTransactions.map((tx) => {
                const isSelected = selectedTxIds.has(tx.id);
                return (
                  <div
                    key={tx.id}
                    onClick={canEdit ? (e) => handleToggleSelectOne(tx.id, e) : undefined}
                    className={`p-3.5 transition-colors ${isSelected ? 'bg-red-50/60' : 'hover:bg-slate-50/80'
                      } ${canEdit ? 'cursor-pointer' : ''}`}
                  >
                    {/* Card Top Row: Checkbox + Category + Date + Delete */}
                    <div className="flex items-center justify-between gap-2 mb-2">
                      <div className="flex items-center gap-2 min-w-0">
                        {canEdit && (
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={(e) => handleToggleSelectOne(tx.id, e)}
                            onClick={(e) => e.stopPropagation()}
                            className="w-4 h-4 rounded border-slate-300 text-asmm-red focus:ring-asmm-red cursor-pointer shrink-0"
                            title={`Pilih ${tx.description}`}
                          />
                        )}
                        <span
                          className={`text-[10px] font-black px-2 py-0.5 rounded uppercase tracking-wider shrink-0 ${tx.type === 'Income'
                            ? 'bg-emerald-50 text-emerald-700 border border-emerald-200/60'
                            : 'bg-rose-50 text-rose-700 border border-rose-200/60'
                            }`}
                        >
                          {tx.category}
                        </span>
                        {(() => {
                          const b = getBankBadge(tx.resolvedBankKey);
                          return (
                            <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded border shrink-0 ${b.bg}`}>
                              {b.label}
                            </span>
                          );
                        })()}
                        <span className="text-[11px] font-mono text-slate-400 shrink-0">
                          {tx.date}
                        </span>
                      </div>

                      {canEdit && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteTx(tx.id, tx.description);
                          }}
                          title="Padam Transaksi"
                          className="p-1.5 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-colors cursor-pointer shrink-0"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>

                    {/* Card Middle Row: Description & Online Gateway Badge */}
                    <div className="mb-2.5">
                      <div className="text-xs font-bold text-slate-900 leading-snug">
                        {tx.description}
                      </div>
                      {tx.toyibBillCode && (
                        <div className="mt-1.5 flex items-center gap-1.5 flex-wrap">
                          <span className="text-[10px] bg-emerald-50 text-emerald-800 font-mono font-bold px-2 py-0.5 rounded border border-emerald-200">
                            Rujukan: {tx.toyibBillCode}
                          </span>
                          {tx.status === 'Menunggu Pengesahan' && (
                            <span className="text-[10px] bg-amber-50 text-amber-800 font-bold px-2 py-0.5 rounded border border-amber-200 animate-pulse">
                              Menunggu Pengesahan
                            </span>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Card Bottom Row: Payer & Amount Highlight */}
                    <div className="flex items-center justify-between gap-2 pt-2 border-t border-slate-100">
                      <div className="flex items-center gap-1.5 text-[11px] text-slate-500 min-w-0">
                        <span className="truncate">{tx.payerOrPayee}</span>
                        {tx.receiptUrl ? (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleViewReceipt(tx);
                            }}
                            className="inline-flex items-center gap-1 text-[10px] text-slate-700 hover:text-slate-900 bg-slate-100 hover:bg-slate-200 active:scale-95 px-2 py-0.5 rounded-lg border border-slate-200 font-bold shrink-0 transition-all cursor-pointer shadow-3xs"
                            title="Klik untuk lihat bukti resit"
                          >
                            <FolderGit2 className="w-3 h-3 text-asmm-red shrink-0" />
                            <span>Resit</span>
                          </button>
                        ) : null}
                      </div>

                      <div
                        className={`font-mono font-black text-sm shrink-0 ${tx.type === 'Income' ? 'text-emerald-700' : 'text-rose-700'
                          }`}
                      >
                        {tx.type === 'Income' ? '+' : '-'} RM {tx.amount}.00
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* 2. DESKTOP / TABLET DATA TABLE (>= md) */}
        <div className="hidden md:block overflow-x-auto">
          <table className="w-full text-left text-xs border-collapse">
            <thead>
              <tr className="bg-slate-50 text-slate-600 border-b border-slate-200 uppercase text-[10px] tracking-wider font-extrabold">
                {canEdit && (
                  <th className="p-3.5 w-10 text-center no-print">
                    <input
                      type="checkbox"
                      checked={filteredTransactions.length > 0 && selectedTxIds.size === filteredTransactions.length}
                      ref={(el) => {
                        if (el) {
                          el.indeterminate = selectedTxIds.size > 0 && selectedTxIds.size < filteredTransactions.length;
                        }
                      }}
                      onChange={handleToggleSelectAll}
                      className="w-4 h-4 rounded border-slate-300 text-asmm-red focus:ring-asmm-red cursor-pointer"
                      title="Pilih Semua Transaksi"
                    />
                  </th>
                )}
                <th className="p-3.5">Tarikh</th>
                <th className="p-3.5">Akaun Bank</th>
                <th className="p-3.5">Kategori</th>
                <th className="p-3.5">Penerangan Transaksi</th>
                <th className="p-3.5">Pembayar / Penerima</th>
                <th className="p-3.5 text-right">Jumlah (RM)</th>
                <th className="p-3.5">Bukti Resit</th>
                {canEdit && <th className="p-3.5 text-center no-print">Tindakan</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 text-slate-800">
              {filteredTransactions.length === 0 ? (
                <tr>
                  <td colSpan={canEdit ? 9 : 7} className="p-8 text-center text-xs text-slate-500 font-medium">
                    Tiada rekod transaksi dijumpai untuk kategori ini.
                  </td>
                </tr>
              ) : (
                filteredTransactions.map((tx) => {
                  const isSelected = selectedTxIds.has(tx.id);
                  return (
                    <tr
                      key={tx.id}
                      className={`transition-colors ${isSelected ? 'bg-red-50/50 hover:bg-red-50/80' : 'hover:bg-slate-50'}`}
                    >
                      {canEdit && (
                        <td className="p-3.5 text-center no-print" onClick={(e) => e.stopPropagation()}>
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={(e) => handleToggleSelectOne(tx.id, e)}
                            className="w-4 h-4 rounded border-slate-300 text-asmm-red focus:ring-asmm-red cursor-pointer"
                            title={`Pilih ${tx.description}`}
                          />
                        </td>
                      )}
                      <td className="p-3.5 font-mono text-slate-500 whitespace-nowrap">{tx.date}</td>
                      <td className="p-3.5 whitespace-nowrap">
                        {(() => {
                          const b = getBankBadge(tx.resolvedBankKey);
                          return (
                            <span className={`inline-flex items-center gap-1.5 text-[10px] font-bold px-2 py-0.5 rounded-lg border ${b.bg}`}>
                              <span className={`w-1.5 h-1.5 rounded-full ${b.dot}`} />
                              <span>{b.label}</span>
                            </span>
                          );
                        })()}
                      </td>
                      <td className="p-3.5 font-bold">{tx.category}</td>
                      <td className="p-3.5 max-w-xs">
                        <div className="font-medium text-slate-900">{tx.description}</div>
                        {tx.toyibBillCode && (
                          <div className="mt-1 flex items-center gap-1.5 flex-wrap">
                            <span className="text-[10px] bg-emerald-50 text-emerald-800 font-mono font-bold px-2 py-0.5 rounded border border-emerald-200">
                              Rujukan: {tx.toyibBillCode}
                            </span>
                            {tx.status === 'Menunggu Pengesahan' && (
                              <span className="text-[10px] bg-amber-50 text-amber-800 font-bold px-2 py-0.5 rounded border border-amber-200 animate-pulse">
                                Menunggu Pengesahan
                              </span>
                            )}
                          </div>
                        )}
                      </td>
                      <td className="p-3.5 text-slate-600">{tx.payerOrPayee}</td>
                      <td className={`p-3.5 text-right font-mono font-bold text-sm ${tx.type === 'Income' ? 'text-emerald-700' : 'text-rose-700'
                        }`}>
                        {tx.type === 'Income' ? '+' : '-'} RM {tx.amount}.00
                      </td>
                      <td className="p-3.5">
                        {tx.receiptUrl ? (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleViewReceipt(tx);
                            }}
                            className="inline-flex items-center gap-1.5 text-[11px] text-slate-700 hover:text-slate-900 bg-slate-100 hover:bg-slate-200 active:scale-95 px-2.5 py-1 rounded-xl border border-slate-200 font-bold transition-all cursor-pointer shadow-3xs"
                            title="Klik untuk lihat bukti resit transaksi"
                          >
                            <FolderGit2 className="w-3.5 h-3.5 text-asmm-red shrink-0" />
                            <span>Salinan</span>
                          </button>
                        ) : (
                          <span className="text-slate-400 text-[10px]">Tiada Resit</span>
                        )}
                      </td>
                      {canEdit && (
                        <td className="p-3.5 text-center no-print">
                          <button
                            onClick={() => handleDeleteTx(tx.id, tx.description)}
                            title="Padam Transaksi"
                            className="p-1.5 text-rose-600 hover:bg-rose-50 rounded-lg transition-colors cursor-pointer"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </td>
                      )}
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>



      {/* NEW TRANSACTION MODAL */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title="Tambah Rekod Buku Tunai"
      >
        <form onSubmit={handleSaveTransaction} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1">Jenis Transaksi</label>
              <select
                value={type}
                onChange={(e) => setType(e.target.value as TransactionType)}
                className="w-full px-3 py-2 rounded-xl bg-slate-50 border border-slate-200 text-xs text-slate-900 font-medium"
              >
                <option value="Income">Masuk</option>
                <option value="Expense">Keluar</option>
              </select>
            </div>

            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1">Kategori</label>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className="w-full px-3 py-2 rounded-xl bg-slate-50 border border-slate-200 text-xs text-slate-900 font-medium"
              >
                <option value="Yuran Bulanan">Yuran Bulanan</option>
                <option value="Yuran Pendaftaran">Yuran Pendaftaran</option>
                <option value="Yuran Bengkung">Yuran Bengkung</option>
                <option value="Sumbangan">Sumbangan</option>
                <option value="Sewa Gelanggang">Sewa Gelanggang</option>
                <option value="Pembelian Alatan">Pembelian Alatan</option>
                <option value="Acara & Sijil">Acara & Sijil</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-xs font-bold text-slate-700 mb-1">Akaun Bank / Entiti</label>
            <select
              value={newTxAccount}
              onChange={(e) => setNewTxAccount(e.target.value as any)}
              className="w-full px-3 py-2 rounded-xl bg-slate-50 border border-slate-200 text-xs text-slate-900 font-medium"
            >
              <option value="persatuan">Akaun Induk Persatuan ({persatuanSettings.bankName || 'Bank Islam'} - {persatuanSettings.accountNo || '02011010087654'})</option>
              <option value="pusaka_warisan">Gelanggang Pusaka Warisan ({pusakaDetail?.qrBankName || 'Maybank'} - {pusakaDetail?.qrAccountNo || '152068654321'})</option>
              <option value="wadi_annour">Gelanggang Wadi AnNour ({wadiDetail?.qrBankName || 'Bank Islam'} - {wadiDetail?.qrAccountNo || '02093010041234'})</option>
            </select>
            <p className="text-[10px] text-slate-500 mt-1">
              Transaksi akan direkodkan di bawah buku tunai akaun bank yang dipilih.
            </p>
          </div>

          <div>
            <label className="block text-xs font-bold text-slate-700 mb-1">Penerangan Transaksi</label>
            <input
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Contoh: Sewa Gelanggang Ogos 2026"
              className="w-full px-3.5 py-2 rounded-xl bg-slate-50 border border-slate-200 text-xs text-slate-900"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1">Jumlah (RM)</label>
              <input
                type="number"
                value={amount}
                onChange={(e) => setAmount(Number(e.target.value))}
                className="w-full px-3.5 py-2 rounded-xl bg-slate-50 border border-slate-200 text-xs font-mono text-emerald-700 font-bold"
              />
            </div>

            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1">Pembayar / Penerima</label>
              <input
                type="text"
                value={payerOrPayee}
                onChange={(e) => setPayerOrPayee(e.target.value)}
                placeholder="Contoh: Pengurusan Gelanggang"
                className="w-full px-3.5 py-2 rounded-xl bg-slate-50 border border-slate-200 text-xs text-slate-900"
              />
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-3">
            <button
              type="button"
              onClick={() => setIsModalOpen(false)}
              className="px-4 py-2 text-xs font-semibold text-slate-600 hover:text-slate-900"
            >
              Batal
            </button>
            <button
              type="submit"
              className="px-5 py-2 bg-asmm-red text-white font-bold text-xs rounded-xl hover:bg-asmm-crimson"
            >
              Simpan Rekod Transaksi
            </button>
          </div>
        </form>
      </Modal>

      {/* SINGLE DELETE CONFIRMATION MODAL */}
      {confirmDeleteTxId && (
        <Modal
          isOpen={!!confirmDeleteTxId}
          onClose={() => !isDeleting && setConfirmDeleteTxId(null)}
          title="Sahkan Pemadaman Transaksi"
          maxWidth="max-w-md"
        >
          <div className="space-y-4 pt-1">
            <div className="flex items-start gap-3.5 p-3.5 bg-rose-50/70 border border-rose-200/80 rounded-2xl">
              <div className="p-2 bg-rose-100 text-rose-600 rounded-xl shrink-0 mt-0.5">
                <Trash2 className="w-5 h-5" />
              </div>
              <div className="space-y-1 text-left">
                <div className="text-xs font-bold text-rose-950">
                  Padam transaksi ini?
                </div>
                <p className="text-[11px] text-rose-800 leading-relaxed font-normal">
                  <strong className="font-semibold text-slate-900">{confirmDeleteTxId.desc}</strong>
                </p>
                <p className="text-[10px] text-rose-600 font-medium">Tindakan ini kekal dan akan memadam transaksi daripada aaPanel.</p>
              </div>
            </div>
            <div className="flex justify-end gap-2.5 pt-2 border-t border-slate-100">
              <button
                type="button"
                disabled={isDeleting}
                onClick={() => setConfirmDeleteTxId(null)}
                className="px-4 py-2 text-xs font-semibold text-slate-600 hover:text-slate-900 cursor-pointer border-none bg-slate-100 hover:bg-slate-200 rounded-xl disabled:opacity-50"
              >
                Batal
              </button>
              <button
                type="button"
                disabled={isDeleting}
                onClick={confirmDeleteTx}
                className="px-5 py-2 bg-rose-600 text-white font-bold text-xs rounded-xl hover:bg-rose-700 transition-all flex items-center gap-1.5 cursor-pointer border-none disabled:opacity-50 shadow-xs"
              >
                {isDeleting ? (
                  <span>Memadam...</span>
                ) : (
                  <>
                    <Trash2 className="w-3.5 h-3.5" />
                    <span>Padam Rekod</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* BULK DELETE CONFIRMATION MODAL */}
      {isBulkDeleteModalOpen && (
        <Modal
          isOpen={isBulkDeleteModalOpen}
          onClose={() => !isDeleting && setIsBulkDeleteModalOpen(false)}
          title="Sahkan Pemadaman Pukal Transaksi"
          maxWidth="max-w-md"
        >
          <div className="space-y-4 pt-1">
            <div className="flex items-start gap-3.5 p-3.5 bg-rose-50/70 border border-rose-200/80 rounded-2xl">
              <div className="p-2.5 bg-rose-100 text-rose-600 rounded-xl shrink-0 mt-0.5">
                <Trash2 className="w-5 h-5" />
              </div>
              <div className="space-y-1 text-left">
                <div className="text-xs font-bold text-rose-950">
                  Padam {selectedTxIds.size} rekod transaksi kewangan?
                </div>
                <p className="text-[11px] text-rose-800 leading-relaxed font-normal">
                  Tindakan ini akan memadam rekod transaksi yang dipilih secara kekal daripada buku tunai dan pangkalan data aaPanel.
                </p>
              </div>
            </div>

            <div className="p-3.5 bg-slate-50 border border-slate-200/80 rounded-2xl space-y-1.5 text-xs">
              <div className="flex justify-between text-slate-600">
                <span>Bilangan rekod dipilih:</span>
                <strong className="text-slate-900 font-bold">{selectedTxIds.size} rekod</strong>
              </div>
              <div className="flex justify-between text-slate-600">
                <span>Nilai keseluruhan:</span>
                <strong className="text-slate-900 font-mono font-extrabold text-sm">RM {selectedAmountTotal}.00</strong>
              </div>
            </div>

            <div className="flex items-center justify-end gap-2.5 pt-2 border-t border-slate-100">
              <button
                type="button"
                disabled={isDeleting}
                onClick={() => setIsBulkDeleteModalOpen(false)}
                className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-bold rounded-xl transition-all cursor-pointer border-none disabled:opacity-50"
              >
                Batal
              </button>
              <button
                type="button"
                disabled={isDeleting}
                onClick={confirmBulkDelete}
                className="px-4 py-2 bg-rose-600 hover:bg-rose-700 text-white text-xs font-extrabold rounded-xl transition-all shadow-xs flex items-center gap-1.5 cursor-pointer border-none disabled:opacity-50"
              >
                {isDeleting ? (
                  <span>Memadam Pukal...</span>
                ) : (
                  <>
                    <Trash2 className="w-3.5 h-3.5" />
                    <span>Ya, Padam ({selectedTxIds.size}) Rekod</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* MODAL LIHAT BUKTI RESIT TRANSAKSI */}
      <Modal
        isOpen={!!selectedReceiptTx}
        onClose={() => {
          setSelectedReceiptTx(null);
          setActiveReceiptUrl('');
        }}
        title={`Bukti Resit: ${selectedReceiptTx?.category || 'Transaksi Kewangan'}`}
        maxWidth="max-w-2xl"
      >
        {selectedReceiptTx && (
          <div className="p-4 sm:p-6 space-y-4">
            {/* Transaction metadata card */}
            <div className="flex flex-wrap items-center justify-between gap-3 p-3.5 bg-slate-50 rounded-2xl border border-slate-200">
              <div className="min-w-0">
                <div className="text-xs font-black text-slate-900 truncate">
                  {selectedReceiptTx.description}
                </div>
                <div className="text-[11px] text-slate-600 mt-0.5">
                  Pembayar / Penerima: <strong className="text-slate-800">{selectedReceiptTx.payerOrPayee}</strong> • Tarikh: <span className="font-mono">{selectedReceiptTx.date}</span>
                </div>
                {selectedReceiptTx.toyibBillCode && (
                  <div className="text-[10px] font-mono text-emerald-700 font-bold mt-0.5">
                    Rujukan: {selectedReceiptTx.toyibBillCode}
                  </div>
                )}
              </div>

              <div className="flex items-center gap-2">
                <div className="text-right">
                  <div className="text-sm font-black font-mono text-slate-900">
                    {selectedReceiptTx.type === 'Income' ? '+' : '-'} RM {selectedReceiptTx.amount}.00
                  </div>
                  <span className="inline-block text-[10px] font-bold px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">
                    {selectedReceiptTx.status || 'Disahkan'}
                  </span>
                </div>

                {activeReceiptUrl && (activeReceiptUrl.startsWith('data:') || activeReceiptUrl.startsWith('http')) && (
                  <a
                    href={activeReceiptUrl}
                    download={`Resit_${selectedReceiptTx.category.replace(/\s+/g, '_')}_${selectedReceiptTx.date}.pdf`}
                    className="p-2 bg-white hover:bg-slate-100 text-slate-700 rounded-xl border border-slate-200 transition-colors flex items-center gap-1 text-xs font-bold no-underline cursor-pointer"
                    title="Muat Turun Resit"
                  >
                    <Download className="w-3.5 h-3.5" />
                    <span className="hidden sm:inline">Muat Turun</span>
                  </a>
                )}
              </div>
            </div>

            {/* Receipt Preview Area */}
            <div className="rounded-2xl border border-slate-200 overflow-hidden bg-slate-100/60 p-2">
              {activeReceiptUrl && (activeReceiptUrl.startsWith('data:image/') || activeReceiptUrl.match(/\.(png|jpe?g|webp|gif)$/i)) ? (
                <img
                  src={activeReceiptUrl}
                  alt="Bukti Resit Bayaran"
                  className="max-h-[500px] w-auto mx-auto rounded-xl object-contain shadow-xs bg-white"
                />
              ) : activeReceiptUrl && (activeReceiptUrl.startsWith('data:application/pdf') || activeReceiptUrl.match(/\.pdf$/i) || activeReceiptUrl.startsWith('http')) ? (
                <iframe
                  src={activeReceiptUrl}
                  className="w-full h-[450px] rounded-xl border border-slate-200 bg-white"
                  title="Pratonton Resit PDF"
                />
              ) : (
                /* Digitized Official Certificate / Cashbook Voucher Card */
                <div className="p-6 bg-white rounded-xl border border-slate-200 space-y-4 text-center">
                  <div className="flex items-center justify-center gap-3 border-b border-slate-100 pb-3">
                    <img src="/logo-pssasmm.png" alt="Logo ASMM" className="w-12 h-12 object-contain shrink-0" />
                    <div className="text-left">
                      <div className="text-xs font-black uppercase text-slate-900 leading-tight">
                        PERSATUAN SENI SILAT AWANG SULUNG MERAH MUDA KEDAH
                      </div>
                      <div className="text-[10px] font-mono font-bold text-asmm-red">
                        ROS: PPM-007-02-28082022
                      </div>
                    </div>
                  </div>

                  <div className="py-2">
                    <div className="inline-flex items-center gap-1.5 px-3 py-1 bg-emerald-50 text-emerald-800 rounded-full text-xs font-bold border border-emerald-200 mb-2">
                      <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                      <span>Salinan Sah Buku Tunai Rasmi Pertubuhan</span>
                    </div>
                    <h4 className="text-sm font-bold text-slate-900">{selectedReceiptTx.description}</h4>
                    <p className="text-xs text-slate-500 mt-1">
                      Kategori: <strong className="text-slate-700">{selectedReceiptTx.category}</strong> • Gelanggang: <strong className="text-slate-700">{selectedReceiptTx.gelanggang}</strong>
                    </p>
                  </div>

                  <div className="p-3 bg-slate-50 rounded-xl border border-slate-200 text-xs font-mono max-w-sm mx-auto flex justify-between items-center">
                    <span className="text-slate-600 font-sans">Jumlah Transaksi:</span>
                    <strong className="text-base font-black text-emerald-700">RM {selectedReceiptTx.amount}.00</strong>
                  </div>

                  <p className="text-[11px] text-slate-400">
                    Rekod baucar ini difailkan di bawah pangkalan data audit rasmi ASMM dan disahkan oleh Bendahari Kehormat.
                  </p>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="flex justify-end pt-2 border-t border-slate-100">
              <button
                type="button"
                onClick={() => {
                  setSelectedReceiptTx(null);
                  setActiveReceiptUrl('');
                }}
                className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-bold rounded-xl transition-all cursor-pointer border-none"
              >
                Tutup
              </button>
            </div>
          </div>
        )}
      </Modal>

      {/* ROS AUDIT REPORT & CASHBOOK PRINT DOCUMENT PORTAL (Mounted directly to document.body) */}
      {typeof document !== 'undefined' && createPortal(
        <div className="print-document-portal hidden print:block font-sans text-slate-900 relative w-full bg-white">
          <div className="max-w-4xl mx-auto p-8 pt-6 space-y-6">
            {/* 1. Header Format Surat Rasmi ASMM */}
            <div className="relative pb-4 border-b-4 border-double border-slate-900 pt-2">
              <div className="flex items-center gap-4">
                <img src="/logo-pssasmm.png" alt="Logo PSSASMM" className="w-20 h-20 object-contain shrink-0" />
                <div className="space-y-1 text-left min-w-0">
                  <h1 className="text-base font-black tracking-tight text-slate-900 uppercase leading-tight">
                    PERSATUAN SENI SILAT AWANG SULUNG MERAH MUDA KEDAH
                  </h1>
                  <div className="text-xs font-extrabold text-asmm-red uppercase tracking-wider font-mono">
                    PENDAFTARAN ROS: PPM-007-02-28082022
                  </div>
                  <p className="text-xs text-slate-600 leading-tight">
                    263, Persiaran Utama 1/9, Kulim Utama, 09000 Kulim, Kedah Darul Aman
                  </p>
                  <p className="text-xs text-slate-500 font-mono">
                    Tel: 012-4475157 • Emel: rasmi@asmm.org.my • Portal: asmm.pages.dev
                  </p>
                </div>
              </div>
            </div>

            {/* 2. Document Title & Generation Meta */}
            <div className="text-center space-y-1 py-1">
              <h2 className="text-lg font-black text-slate-900 uppercase tracking-wide underline decoration-2 underline-offset-4">
                {selectedAccountKey === 'all'
                  ? 'PENYATA TAHUNAN AUDIT KEWANGAN & BUKU TUNAI (KONSOLIDASI SEMUA AKAUN)'
                  : `PENYATA BUKU TUNAI - ${selectedAccount.label.toUpperCase()}`}
              </h2>
              <div className="text-xs text-slate-700 font-mono">
                <strong>{selectedAccount.bankName}</strong> • No. Akaun: <strong>{selectedAccount.accountNo}</strong>
                {selectedAccountKey !== 'all' && ` • Pemegang: ${selectedAccount.accountHolder}`}
              </div>
              <div className="flex items-center justify-between text-xs text-slate-600 pt-1 font-mono">
                <span>Tarikh Dijana: {new Date().toLocaleDateString('ms-MY', { day: '2-digit', month: 'long', year: 'numeric' })}</span>
                <span>
                  Kategori: {filterType === 'Income' ? 'Wang Masuk Sahaja' : filterType === 'Expense' ? 'Wang Keluar Sahaja' : 'Semua Transaksi'}
                  {selectedTxIds.size > 0 ? ` (${selectedTxIds.size} rekod dipilih)` : ''}
                </span>
                <span>Pegawai: {currentUser?.name || 'Bendahari'}</span>
              </div>
            </div>

            {/* 3. Executive Financial Summary */}
            <div className="grid grid-cols-3 gap-3 border border-slate-300 rounded-xl p-3 bg-slate-50">
              <div className="text-center border-r border-slate-200 pr-2">
                <div className="text-[10px] font-black text-slate-500 uppercase">Jumlah Pendapatan</div>
                <div className="text-base font-black text-emerald-700 font-mono">
                  RM {totalIncome.toLocaleString('ms-MY', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </div>
                <div className="text-[10px] text-slate-400 font-medium">{incomeCount} Transaksi Masuk</div>
              </div>
              <div className="text-center border-r border-slate-200 px-2">
                <div className="text-[10px] font-black text-slate-500 uppercase">Jumlah Perbelanjaan</div>
                <div className="text-base font-black text-rose-700 font-mono">
                  RM {totalExpense.toLocaleString('ms-MY', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </div>
                <div className="text-[10px] text-slate-400 font-medium">{expenseCount} Transaksi Keluar</div>
              </div>
              <div className="text-center pl-2">
                <div className="text-[10px] font-black text-slate-500 uppercase">Baki Bersih Buku Tunai</div>
                <div className="text-base font-black text-slate-900 font-mono">
                  RM {netBalance.toLocaleString('ms-MY', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </div>
                <div className="text-[10px] text-slate-400 font-medium">Baki Terkini Pertubuhan</div>
              </div>
            </div>

            {/* 4. Ledger Table */}
            <div className="space-y-2">
              <div className="text-xs font-bold text-slate-700 uppercase tracking-wide">
                Rekod Transaksi Kewangan ({printTransactions.length} Rekod)
              </div>
              <table className="w-full text-xs border-collapse border border-slate-300">
                <thead>
                  <tr className="bg-slate-100 text-slate-800 border-b border-slate-300 font-bold uppercase text-[10px]">
                    <th className="p-2 border border-slate-300 text-center w-8">Bil</th>
                    <th className="p-2 border border-slate-300 text-center w-24">Tarikh</th>
                    {selectedAccountKey === 'all' && (
                      <th className="p-2 border border-slate-300 text-left w-24">Akaun Bank</th>
                    )}
                    <th className="p-2 border border-slate-300 text-left w-28">Kategori</th>
                    <th className="p-2 border border-slate-300 text-left">Penerangan / Butiran</th>
                    <th className="p-2 border border-slate-300 text-left w-36">Pembayar / Penerima</th>
                    <th className="p-2 border border-slate-300 text-right w-24">Masuk (RM)</th>
                    <th className="p-2 border border-slate-300 text-right w-24">Keluar (RM)</th>
                  </tr>
                </thead>
                <tbody>
                  {printTransactions.length === 0 ? (
                    <tr>
                      <td colSpan={selectedAccountKey === 'all' ? 8 : 7} className="p-4 text-center text-slate-500 border border-slate-300">
                        Tiada transaksi untuk dicetak.
                      </td>
                    </tr>
                  ) : (
                    printTransactions.map((tx, idx) => (
                      <tr key={tx.id} className="border-b border-slate-200 even:bg-slate-50/50 break-inside-avoid">
                        <td className="p-2 border border-slate-300 text-center font-mono text-slate-500">{idx + 1}</td>
                        <td className="p-2 border border-slate-300 text-center font-mono whitespace-nowrap">{tx.date}</td>
                        {selectedAccountKey === 'all' && (
                          <td className="p-2 border border-slate-300 font-bold text-[10px] text-slate-700 whitespace-nowrap">
                            {getBankBadge(tx.resolvedBankKey).label}
                          </td>
                        )}
                        <td className="p-2 border border-slate-300 font-bold text-slate-800">{tx.category}</td>
                        <td className="p-2 border border-slate-300">
                          <div className="font-medium text-slate-900">{tx.description}</div>
                          {tx.toyibBillCode && (
                            <div className="text-[10px] text-emerald-700 font-mono font-semibold">
                              Rujukan: {tx.toyibBillCode}
                            </div>
                          )}
                        </td>
                        <td className="p-2 border border-slate-300 text-slate-700">{tx.payerOrPayee}</td>
                        <td className="p-2 border border-slate-300 text-right font-mono font-bold text-emerald-700">
                          {tx.type === 'Income' ? tx.amount.toFixed(2) : '-'}
                        </td>
                        <td className="p-2 border border-slate-300 text-right font-mono font-bold text-rose-700">
                          {tx.type === 'Expense' ? tx.amount.toFixed(2) : '-'}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
                <tfoot>
                  <tr className="bg-slate-100 font-bold border-t-2 border-slate-400">
                    <td colSpan={selectedAccountKey === 'all' ? 6 : 5} className="p-2 border border-slate-300 text-right uppercase text-[11px]">
                      Jumlah Bagi Senarai Ini:
                    </td>
                    <td className="p-2 border border-slate-300 text-right font-mono text-emerald-800 text-xs">
                      RM {printIncomeTotal.toFixed(2)}
                    </td>
                    <td className="p-2 border border-slate-300 text-right font-mono text-rose-800 text-xs">
                      RM {printExpenseTotal.toFixed(2)}
                    </td>
                  </tr>
                  <tr className="bg-slate-200/80 font-black border-t border-slate-300">
                    <td colSpan={selectedAccountKey === 'all' ? 6 : 5} className="p-2 border border-slate-300 text-right uppercase text-[11px]">
                      Baki Bersih Senarai Ini:
                    </td>
                    <td colSpan={2} className="p-2 border border-slate-300 text-right font-mono text-slate-900 text-xs">
                      RM {(printIncomeTotal - printExpenseTotal).toFixed(2)}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>

            {/* 5. Sign-off and Internal Audit Verification Section */}
            <div className="pt-6 border-t border-slate-300 break-inside-avoid space-y-4">
              <div className="text-xs font-bold text-slate-800 uppercase tracking-wide">
                Perakuan & Pengesahan Audit Pertubuhan (JPPM / ROS)
              </div>
              <div className="grid grid-cols-3 gap-6 text-center text-xs pt-2">
                <div className="space-y-12">
                  <div className="font-bold text-slate-700">Disediakan Oleh:</div>
                  <div className="border-t border-slate-400 pt-1">
                    <div className="font-bold text-slate-900 uppercase">MOHD AIZAT BIN YUSOFF</div>
                    <div className="text-[11px] text-slate-500">Bendahari Kehormat ASMM</div>
                    <div className="text-[10px] text-slate-400 mt-0.5">Tarikh: _______________</div>
                  </div>
                </div>

                <div className="space-y-12">
                  <div className="font-bold text-slate-700">Diaudit & Disemak Oleh:</div>
                  <div className="border-t border-slate-400 pt-1">
                    <div className="font-bold text-slate-900 uppercase">PEMERIKSA KIRA-KIRA</div>
                    <div className="text-[11px] text-slate-500">Jawatankuasa Audit Dalaman</div>
                    <div className="text-[10px] text-slate-400 mt-0.5">Tarikh: _______________</div>
                  </div>
                </div>

                <div className="space-y-12">
                  <div className="font-bold text-slate-700">Disahkan & Diperakui:</div>
                  <div className="border-t border-slate-400 pt-1">
                    <div className="font-bold text-slate-900 uppercase">MOHD HAFIFE BIN ABD HALIM</div>
                    <div className="text-[11px] text-slate-500">Pengerusi / Yang Dipertua</div>
                    <div className="text-[10px] text-slate-400 mt-0.5">Tarikh: _______________</div>
                  </div>
                </div>
              </div>
            </div>

            {/* 6. Footer Disclaimer & ROS Compliance Note */}
            <div className="pt-4 text-center text-[10px] text-slate-500 border-t border-slate-200">
              Penyata ini dijana secara rasmi melalui Sistem Pengurusan ASMM untuk tujuan pembentangan Mesyuarat Agung Tahunan (AGM) dan pemfailan tahunan Jabatan Pendaftaran Pertubuhan Malaysia (JPPM eROSES) di bawah Seksyen 7, Akta Pertubuhan 1966.
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};
